import {
    useImperativeHandle,
    forwardRef,
    useEffect,
    useState,
    Fragment,
    useMemo,
    useRef
} from "react";
import {
    ScrollView,
    Platform,
    View
} from "react-native";
import type IMenuProps from "./type";
import type {
    MenuButtonCollapseOverrides,
    MenuWebPropsType,
    IMenuRef
} from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import MenuButton from "./components/menuButton";
import {
    getMenuButtonsWithCollapse,
    updateMenuButtonCollapse
} from "./util";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    NCoreUIKitMenu
} from "../../core/hooks";
import type {
    RefForwardingComponent,
    NCoreUIKitIcon
} from "../../types";
import {
    useMotionTiming
} from "../../helpers/motion";
import {
    useActiveRouteName
} from "../../helpers/navigation";
import {
    SafeAreaView
} from "react-native-safe-area-context";
import {
    X as XIcon
} from "lucide-react-native";
import {
    warnOnce
} from "../../utils/development";
import {
    Portal
} from "../../helpers/portalize";
import AnimatedIcon from "../animatedIcon";
import Separator from "../separator";
import SiteLogo from "../siteLogo";
import Button from "../button";
import Sheet from "../sheet";

const MenuCloseIcon: NCoreUIKitIcon = ({
    customColor,
    color,
    size
}) => {
    return <AnimatedIcon
        customColor={customColor}
        animationTrigger="hover"
        animation="signature"
        color={color}
        icon={XIcon}
        size={size}
    />;
};

const Menu: RefForwardingComponent<IMenuRef, IMenuProps> = ({
    renderHeaderSeparator: renderHeaderSeparatorProp,
    renderHeaderSeperator: renderHeaderSeperatorProp,
    separatorProps: separatorPropsProp,
    isWorkWithSafeAreaView = true,
    isWorkWithAnimation = true,
    portalName = "menu-system",
    renderFooter: RenderFooter,
    renderHeader: RenderHeader,
    customBackgroundColorKey,
    isWorkWithPortal = true,
    isWorkWithModal = true,
    customBackgroundColor,
    isAutoClosedOnClosed,
    isShowCollapseButton,
    isShowCollabsButton,
    close: closeAction,
    glassEffect = 0,
    customLocalize,
    renderSiteLogo,
    seperatorProps,
    isCollapsible,
    siteLogoProps,
    isAutoClosed,
    customTheme,
    isCollapse,
    modalProps,
    navigation,
    onClosed,
    onOpened,
    buttons,
    onClose,
    onOpen,
    style,
    logo,
    id,
    ...props
}, ref) => {
    const {
        shapeConfigs,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        direction,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const {
        duration: panelEnterDuration
    } = useMotionTiming("panelEnter", customTheme);

    const isCurrentShowCollapseButton = isShowCollapseButton ?? isShowCollabsButton ?? true;

    const currentRenderHeaderSeparator = renderHeaderSeparatorProp ?? renderHeaderSeperatorProp;

    const separatorProps = separatorPropsProp ?? seperatorProps;

    const activeRouteName = useActiveRouteName({
        navigation
    });

    const {
        contentContainer: contentContainerDynamicStyle,
        headerContainer: headerContainerDynamicStyle,
        container: containerDynamicStyle,
        footer: footerDynamicStyle
    } = useStyles({
        customBackgroundColorKey,
        customBackgroundColor,
        glassEffect,
        colors,
        spaces
    });

    const [
        isOpen,
        setIsOpen
    ] = useState(false);

    const [
        collapseOverrides,
        setCollapseOverrides
    ] = useState<MenuButtonCollapseOverrides>({});

    const slideDuration = isWorkWithAnimation ? panelEnterDuration : 0;

    const isOpenRef = useRef(false);

    const onOpenedRef = useRef(onOpened);

    const onClosedRef = useRef(onClosed);

    const currentButtons = useMemo(() => getMenuButtonsWithCollapse({
        buttons: buttons ?? [],
        collapseOverrides
    }), [
        collapseOverrides,
        buttons
    ]);

    const webProps: MenuWebPropsType = Platform.OS === "web" ? {
        dir: direction
    } : {};

    useEffect(() => {
        if(isShowCollabsButton === undefined) {
            return;
        }

        warnOnce({
            message: "Menu: \"isShowCollabsButton\" is deprecated and will be removed in the next major version. Use \"isShowCollapseButton\" instead."
        });
    }, [
        isShowCollabsButton
    ]);

    useEffect(() => {
        if(renderHeaderSeperatorProp === undefined) {
            return;
        }

        warnOnce({
            message: "Menu: \"renderHeaderSeperator\" is deprecated and will be removed in the next major version. Use \"renderHeaderSeparator\" instead."
        });
    }, [
        renderHeaderSeperatorProp
    ]);

    useEffect(() => {
        if(seperatorProps === undefined) {
            return;
        }

        warnOnce({
            message: "Menu: \"seperatorProps\" is deprecated and will be removed in the next major version. Use \"separatorProps\" instead."
        });
    }, [
        seperatorProps
    ]);

    useEffect(() => {
        if(isCollapsible === undefined) {
            return;
        }

        warnOnce({
            message: "Menu: \"isCollapsible\" currently has no effect. Set \"isCollapsible\" on the entries of \"buttons\" to make a group collapsible."
        });
    }, [
        isCollapsible
    ]);

    useEffect(() => {
        if(logo === undefined) {
            return;
        }

        warnOnce({
            message: "Menu: \"logo\" currently has no effect. Use \"siteLogoProps\" or \"renderSiteLogo\" to set up the header logo."
        });
    }, [
        logo
    ]);

    useEffect(() => {
        if(isAutoClosedOnClosed === undefined || NCoreUIKitMenu.get({
            id
        })) {
            return;
        }

        warnOnce({
            message: "Menu: \"isAutoClosedOnClosed\" currently has no effect on a menu that is not registered with NCoreUIKitMenu.load. Update your own state in \"onClosed\" instead."
        });
    }, [
        isAutoClosedOnClosed
    ]);

    useEffect(() => {
        onOpenedRef.current = onOpened;

        onClosedRef.current = onClosed;
    });

    useImperativeHandle(
        ref,
        () => ({
            close: handleClose,
            open: handleOpen
        })
    );

    useEffect(() => {
        if(isCollapse === undefined) {
            return;
        }

        if(isCollapse) {
            handleOpen();

            return;
        }

        handleClose();
    }, [
        isCollapse
    ]);

    useEffect(() => {
        if(!isOpen) {
            return;
        }

        const timer = setTimeout(() => {
            if(onOpenedRef.current) onOpenedRef.current({
                id
            });
        }, slideDuration);

        return () => {
            clearTimeout(timer);
        };
    }, [
        isOpen
    ]);

    const handleClosed = () => {
        if(onClosedRef.current) onClosedRef.current({
            id
        });
    };

    const handleOpen = () => {
        if(isOpenRef.current) {
            return;
        }

        isOpenRef.current = true;

        setIsOpen(true);

        if(onOpen) onOpen({
            id
        });
    };

    const handleSheetOpenChange = (isNextOpen: boolean) => {
        if(!isNextOpen) {
            handleClose();
        }
    };

    const handleClose = () => {
        if(!isOpenRef.current) {
            return;
        }

        isOpenRef.current = false;

        setIsOpen(false);

        if(onClose) onClose({
            id
        });
    };

    const handleMenuButtonCollapse = ({
        depthMap,
        status
    }: {
        depthMap: Array<number>;
        status: boolean;
    }) => {
        updateMenuButtonCollapse({
            store: NCoreUIKitMenu,
            setCollapseOverrides,
            depthMap,
            status,
            id
        });
    };

    const renderClose = () => {
        if(!isCurrentShowCollapseButton) {
            return null;
        }

        return <Button
            accessibilityLabel={localize("close-menu")}
            customTheme={customTheme}
            spreadBehaviour="free"
            onPress={handleClose}
            icon={MenuCloseIcon}
            variant="ghost"
            type="neutral"
            size="medium"
        />;
    };

    const renderLogo = () => {
        if(renderSiteLogo) {
            return renderSiteLogo();
        }

        return <SiteLogo
            customLocalize={customLocalize}
            customTheme={customTheme}
            size="medium"
            {...siteLogoProps}
        />;
    };

    const renderHeaderSeparator = () => {
        if(currentRenderHeaderSeparator) {
            return currentRenderHeaderSeparator();
        }

        return <Separator
            customTheme={customTheme}
            {...separatorProps}
            style={[
                separatorProps?.style,
                stylesheet.separator
            ]}
            length="100%"
        />;
    };

    const renderHeader = () => {
        if(RenderHeader) {
            return <RenderHeader/>;
        }

        return <Fragment>
            <View
                style={[
                    stylesheet.headerContainer,
                    headerContainerDynamicStyle
                ]}
            >
                {renderLogo()}
                {renderClose()}
            </View>
            {renderHeaderSeparator()}
        </Fragment>;
    };

    const renderButtons = () => {
        if(!currentButtons.length) {
            return null;
        }

        return currentButtons.map((buttonItem, buttonIndex) => {
            return <MenuButton
                closeAction={closeAction ?? (isAutoClosed ? handleClose : undefined)}
                updateMenuButtonCollapse={handleMenuButtonCollapse}
                key={`menu-button-${id}-${buttonIndex}`}
                activeRouteName={activeRouteName}
                customLocalize={customLocalize}
                buttonIndex={buttonIndex}
                customTheme={customTheme}
                buttonItem={buttonItem}
                navigation={navigation}
                depthMap={[
                    buttonIndex
                ]}
                id={id}
            />;
        });
    };

    const renderContent = () => {
        return <ScrollView
            showsHorizontalScrollIndicator={false}
            showsVerticalScrollIndicator={false}
            contentContainerStyle={[
                stylesheet.contentContainer,
                contentContainerDynamicStyle
            ]}
            style={[
                stylesheet.content
            ]}
        >
            {renderButtons()}
        </ScrollView>;
    };

    const renderFooter = () => {
        if(!RenderFooter) {
            return null;
        }

        return <Fragment>
            <Separator
                customTheme={customTheme}
                length="100%"
            />
            <View
                style={[
                    stylesheet.footer,
                    footerDynamicStyle
                ]}
            >
                <RenderFooter/>
            </View>
        </Fragment>;
    };

    const renderSections = () => {
        return <Fragment>
            {renderHeader()}
            {renderContent()}
            {renderFooter()}
        </Fragment>;
    };

    const renderSafeAreaContext = () => {
        if(!isWorkWithSafeAreaView) {
            return renderSections();
        }

        return <SafeAreaView
            style={[
                stylesheet.safeAreaView
            ]}
        >
            {renderSections()}
        </SafeAreaView>;
    };

    const renderDrawer = () => {
        return <View
            {...props}
            {...webProps}
            aria-label={localize("menu")}
            style={[
                style,
                stylesheet.container,
                containerDynamicStyle
            ]}
            role="navigation"
        >
            {renderSafeAreaContext()}
        </View>;
    };

    const renderSheet = () => {
        return <Sheet
            onOpenChange={handleSheetOpenChange}
            width={shapeConfigs.drawerWidth}
            customLocalize={customLocalize}
            compactBehaviour="keepSide"
            isModal={isWorkWithModal}
            customTheme={customTheme}
            onClosed={handleClosed}
            isOpen={isOpen}
            side="start"
        >
            <Sheet.Content
                aria-label={modalProps?.["aria-label"] ?? localize("menu")}
            >
                {renderDrawer()}
            </Sheet.Content>
        </Sheet>;
    };

    if(isWorkWithModal || !isWorkWithPortal) {
        return renderSheet();
    }

    return <Portal
        name={portalName}
    >
        {renderSheet()}
    </Portal>;
};
export default forwardRef(Menu);
